import { ChevronDown, X } from 'lucide-react'
import { useState, useRef, useMemo, useId, useEffect, useCallback } from 'react'

import { useClickOutside } from '@/hooks/use-click-outside'

export interface SearchableSelectOption {
  id: string
  name: string
}

interface SearchableSelectProps {
  options: SearchableSelectOption[]
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  id?: string
  label?: string
  placeholder?: string
  disabled?: boolean
  isLoading?: boolean
  width?: string
  'aria-label'?: string
}

function filterOptions(options: SearchableSelectOption[], query: string): SearchableSelectOption[] {
  if (!query) return options

  const lower = query.toLowerCase()
  return options.filter(
    (opt) => opt.name.toLowerCase().includes(lower) || opt.id.toLowerCase().includes(lower)
  )
}

function getContainerClass(disabled: boolean, isOpen: boolean): string {
  const base =
    'flex items-center w-full border rounded-lg shadow-sm bg-white dark:bg-gray-900 transition-all border-gray-300 dark:border-gray-600'
  const cursorClass = disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'
  const focusClass = isOpen && !disabled ? 'ring-2 ring-primary-500 border-transparent' : ''
  return `${base} ${cursorClass} ${focusClass}`
}

interface KeyDownContext {
  isOpen: boolean
  highlightedIndex: number
  filtered: SearchableSelectOption[]
  setIsOpen: (open: boolean) => void
  setSearchQuery: (query: string) => void
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>
  onSelect: (id: string) => void
}

function handleSearchableKeyDown(e: React.KeyboardEvent<HTMLInputElement>, ctx: KeyDownContext) {
  if (e.key === 'Escape') {
    ctx.setIsOpen(false)
    ctx.setSearchQuery('')
    ctx.setHighlightedIndex(-1)
    return
  }

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (!ctx.isOpen) ctx.setIsOpen(true)
    ctx.setHighlightedIndex((prev) => (prev < ctx.filtered.length - 1 ? prev + 1 : 0))
    return
  }

  if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (!ctx.isOpen) ctx.setIsOpen(true)
    ctx.setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : ctx.filtered.length - 1))
    return
  }

  if (e.key === 'Enter') {
    e.preventDefault()
    if (ctx.highlightedIndex >= 0 && ctx.highlightedIndex < ctx.filtered.length) {
      ctx.onSelect(ctx.filtered[ctx.highlightedIndex].id)
    }
  }
}

function scrollHighlightedIntoView(listboxRef: React.RefObject<HTMLUListElement>, index: number) {
  if (index < 0) return
  const listbox = listboxRef.current
  if (!listbox) return
  const items = listbox.querySelectorAll<HTMLElement>('[role="option"]')
  if (index >= items.length) return
  const item = items.item(index)
  if (typeof item.scrollIntoView === 'function') {
    item.scrollIntoView({ block: 'nearest' })
  }
}

function useSearchableSelectState(
  options: SearchableSelectOption[],
  value: string,
  onChange: (value: string) => void,
  disabled: boolean
) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listboxRef = useRef<HTMLUListElement>(null)
  const listboxId = useId()
  const optionIdPrefix = `${listboxId}-option-`
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const resetState = useCallback(() => {
    setIsOpen(false)
    setSearchQuery('')
    setHighlightedIndex(-1)
  }, [])

  useClickOutside(containerRef, resetState)

  const selectedOption = useMemo(
    () => options.find((opt) => opt.id === value) ?? null,
    [options, value]
  )

  const filtered = useMemo(() => filterOptions(options, searchQuery), [options, searchQuery])

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [filtered])

  useEffect(() => {
    scrollHighlightedIntoView(listboxRef, highlightedIndex)
  }, [highlightedIndex])

  const handleSelect = useCallback(
    (optionId: string) => {
      onChangeRef.current(optionId)
      resetState()
    },
    [resetState]
  )

  const handleInputChange = useCallback((val: string) => {
    setSearchQuery(val)
    setIsOpen(true)
  }, [])

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onChangeRef.current('')
    setSearchQuery('')
    inputRef.current?.focus()
  }, [])

  return {
    isOpen,
    searchQuery,
    highlightedIndex,
    containerRef,
    inputRef,
    listboxRef,
    listboxId,
    optionIdPrefix,
    filtered,
    selectedOption,
    disabled,
    setIsOpen,
    setSearchQuery,
    setHighlightedIndex,
    displayValue: isOpen || searchQuery ? searchQuery : (selectedOption?.name ?? ''),
    activeDescendantId: highlightedIndex >= 0 ? `${optionIdPrefix}${highlightedIndex}` : undefined,
    handleSelect,
    handleInputChange,
    handleClear,
  }
}

function OptionsList({
  options,
  selectedId,
  highlightedIndex,
  searchQuery,
  optionIdPrefix,
  onSelect,
}: {
  options: SearchableSelectOption[]
  selectedId: string
  highlightedIndex: number
  searchQuery: string
  optionIdPrefix: string
  onSelect: (id: string) => void
}) {
  if (options.length === 0) {
    return (
      <li role="status" className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
        {searchQuery ? 'No matches found' : 'No options available'}
      </li>
    )
  }

  return (
    <>
      {options.map((option, index) => {
        const isHighlighted = index === highlightedIndex
        const isSelected = option.id === selectedId

        function getBgClass(): string {
          if (isHighlighted) return 'bg-gray-100 dark:bg-gray-700'
          if (isSelected) return 'bg-primary-50 dark:bg-primary-900/20'
          return 'hover:bg-gray-50 dark:hover:bg-gray-800'
        }
        const bgClass = getBgClass()

        return (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <li
            key={option.id}
            role="option"
            id={`${optionIdPrefix}${index}`}
            aria-selected={isSelected}
            onClick={() => {
              onSelect(option.id)
            }}
            className={`w-full px-4 py-2.5 text-left transition-colors flex items-center justify-between gap-3 group cursor-pointer ${bgClass}`}
          >
            <span className="text-sm text-gray-900 dark:text-gray-100 font-medium">
              {option.name}
            </span>
            {option.id !== option.name && (
              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono group-hover:text-primary-600 dark:group-hover:text-primary-400">
                {option.id}
              </span>
            )}
          </li>
        )
      })}
    </>
  )
}

interface ComputedSelectProps {
  inputDisabled: boolean
  showDropdown: boolean
  showClear: boolean
  listboxControls: string | undefined
  inputPlaceholder: string
  containerWidth: string
}

interface ComputeSelectParams {
  isOpen: boolean
  disabled: boolean
  isLoading: boolean
  value: string
  listboxId: string
  placeholder: string
  width: string | undefined
}

function computeSelectProps(params: ComputeSelectParams): ComputedSelectProps {
  const { isOpen, disabled, isLoading, value, listboxId, placeholder, width } = params
  const inputDisabled = disabled || isLoading
  return {
    inputDisabled,
    showDropdown: isOpen && !inputDisabled,
    showClear: !!value && !disabled,
    listboxControls: isOpen ? listboxId : undefined,
    inputPlaceholder: isLoading ? 'Loading...' : placeholder,
    containerWidth: width ?? '100%',
  }
}

function SelectLabel({ id, label }: { id?: string; label?: string }) {
  if (!label) return null
  return (
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
      {label}
    </label>
  )
}

function SelectDropdown({
  listboxRef,
  listboxId,
  filtered,
  value,
  highlightedIndex,
  searchQuery,
  optionIdPrefix,
  onSelect,
}: {
  listboxRef: React.RefObject<HTMLUListElement>
  listboxId: string
  filtered: SearchableSelectOption[]
  value: string
  highlightedIndex: number
  searchQuery: string
  optionIdPrefix: string
  onSelect: (id: string) => void
}) {
  return (
    <div className="relative h-0">
      <ul
        ref={listboxRef}
        id={listboxId}
        role="listbox"
        onMouseDown={preventListboxMouseDown}
        className="absolute z-30 w-full mt-1 max-h-64 overflow-y-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1"
      >
        <OptionsList
          options={filtered}
          selectedId={value}
          highlightedIndex={highlightedIndex}
          searchQuery={searchQuery}
          optionIdPrefix={optionIdPrefix}
          onSelect={onSelect}
        />
      </ul>
    </div>
  )
}

// Stops the listbox from stealing focus from the input on option mousedown,
// which would fire RHF's blur handler before the click resolves and surface a
// spurious required-field error. Relies on mousedown bubbling from the <li>
// to the <ul> — if any future option handler calls stopPropagation, the
// preventDefault below won't run and the blur-on-select bug returns.
function preventListboxMouseDown(e: React.MouseEvent) {
  e.preventDefault()
}

function ClearButton({ onClick }: { onClick: (e: React.MouseEvent) => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      aria-label="Clear selection"
    >
      <X className="w-4 h-4" />
    </button>
  )
}

function useSearchableSelectHandlers(s: ReturnType<typeof useSearchableSelectState>) {
  const stateRef = useRef(s)
  stateRef.current = s

  const handleFocus = useCallback(() => {
    if (stateRef.current.disabled) return
    stateRef.current.setIsOpen(true)
    if (stateRef.current.selectedOption) stateRef.current.setSearchQuery('')
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const { isOpen, filtered, highlightedIndex } = stateRef.current
    handleSearchableKeyDown(e, {
      isOpen,
      filtered,
      highlightedIndex,
      setIsOpen: stateRef.current.setIsOpen,
      setSearchQuery: stateRef.current.setSearchQuery,
      setHighlightedIndex: stateRef.current.setHighlightedIndex,
      onSelect: stateRef.current.handleSelect,
    })
  }, [])

  return { handleFocus, handleKeyDown }
}

export function SearchableSelect({
  options,
  value,
  onChange,
  onBlur,
  id,
  label,
  placeholder = 'Type to search...',
  disabled = false,
  isLoading = false,
  width,
  'aria-label': ariaLabel,
}: SearchableSelectProps) {
  const s = useSearchableSelectState(options, value, onChange, disabled)
  const { handleFocus, handleKeyDown } = useSearchableSelectHandlers(s)
  const cp = computeSelectProps({
    isOpen: s.isOpen,
    disabled,
    isLoading,
    value,
    listboxId: s.listboxId,
    placeholder,
    width,
  })

  return (
    <div ref={s.containerRef} style={{ width: cp.containerWidth }}>
      <SelectLabel id={id} label={label} />

      <div className={getContainerClass(disabled, s.isOpen)}>
        <input
          ref={s.inputRef}
          id={id}
          type="text"
          value={s.displayValue}
          onChange={(e) => {
            s.handleInputChange(e.target.value)
          }}
          onFocus={handleFocus}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          disabled={cp.inputDisabled}
          placeholder={cp.inputPlaceholder}
          className="flex-1 min-w-0 px-3.5 py-2.5 text-sm outline-none bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 disabled:cursor-not-allowed"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={s.isOpen}
          aria-controls={cp.listboxControls}
          aria-activedescendant={s.activeDescendantId}
          aria-label={ariaLabel}
          autoComplete="off"
        />

        <div className="flex items-center pr-2 gap-1">
          {cp.showClear && <ClearButton onClick={s.handleClear} />}
          <ChevronDown className="w-4 h-4 text-gray-400 pointer-events-none" aria-hidden="true" />
        </div>
      </div>

      {cp.showDropdown && (
        <SelectDropdown
          listboxRef={s.listboxRef}
          listboxId={s.listboxId}
          filtered={s.filtered}
          value={value}
          highlightedIndex={s.highlightedIndex}
          searchQuery={s.searchQuery}
          optionIdPrefix={s.optionIdPrefix}
          onSelect={s.handleSelect}
        />
      )}
    </div>
  )
}
